import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { workflowApi, productsApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, PencilSimple, Trash, CircleNotch, TreeStructure, Package, LinkSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

export default function WorkflowTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignProductId, setAssignProductId] = useState('');
  const [assignTemplateId, setAssignTemplateId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const productId = searchParams.get('assignProduct');
    if (productId && !loading) {
      setAssignProductId(productId);
      setAssignOpen(true);
    }
  }, [searchParams, loading]);

  const loadData = async () => {
    try {
      const [tRes, pRes] = await Promise.all([workflowApi.listTemplates(), productsApi.list()]);
      setTemplates(tRes.data);
      setProducts(pRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workflow template?')) return;
    try {
      await workflowApi.deleteTemplate(id);
      toast.success('Template deleted');
      loadData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleAssign = async () => {
    if (!assignProductId || !assignTemplateId) { toast.error('Select both product and workflow'); return; }
    setAssigning(true);
    try {
      await workflowApi.assignToProduct(assignProductId, assignTemplateId);
      toast.success('Workflow assigned to product');
      setAssignOpen(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
    finally { setAssigning(false); }
  };

  const getProductsUsingTemplate = (templateId) => {
    return products.filter(p => p.workflow_template_id === templateId);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="workflow-templates-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-heading font-semibold ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>Workflows</h1>
            <p className={`mt-1 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              Build sequential workflows with steps, splits, and sub-processes
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setAssignOpen(true)} variant="outline" className={isDark ? 'border-zinc-700 text-zinc-300' : ''} data-testid="assign-workflow-btn">
              <LinkSimple className="w-4 h-4 mr-2" /> Assign to Product
            </Button>
            <Button onClick={() => navigate('/workflows/new')} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="new-workflow-btn">
              <Plus className="w-4 h-4 mr-2" /> New Workflow
            </Button>
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><CircleNotch className="w-8 h-8 animate-spin text-brand-500" /></div>
        ) : templates.length === 0 ? (
          <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
            <CardContent className="py-16 text-center">
              <TreeStructure className={`w-12 h-12 mx-auto mb-3 opacity-30 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>No workflow templates yet</p>
              <Button onClick={() => navigate('/workflows/new')} className="mt-4 bg-brand-600 hover:bg-brand-700 text-white">
                <Plus className="w-4 h-4 mr-2" /> Create Your First Workflow
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(t => {
              const usedBy = getProductsUsingTemplate(t.id);
              const nodeCount = (t.nodes || []).length;
              const edgeCount = (t.edges || []).length;
              const taskCount = (t.nodes || []).filter(n => n.type === 'task').length;
              const gatewayCount = (t.nodes || []).filter(n => ['exclusive', 'parallel', 'sync', 'merge'].includes(n.type)).length;

              return (
                <Card key={t.id} className={`group transition-all hover:shadow-lg ${isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700' : 'bg-white border-zinc-200 hover:border-zinc-300'}`}
                  data-testid={`template-card-${t.id}`}>
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className={`p-4 border-b ${isDark ? 'border-zinc-800' : 'border-zinc-100'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className={`text-sm font-semibold truncate ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{t.name}</h3>
                          {t.description && <p className={`text-xs mt-0.5 line-clamp-2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t.description}</p>}
                        </div>
                        <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-mono ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-100 text-zinc-500'}`}>
                          v{t.version || 1}
                        </span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className={`text-lg font-bold ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>{taskCount}</p>
                          <p className={`text-[10px] ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>Steps</p>
                        </div>
                        <div>
                          <p className={`text-lg font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{gatewayCount}</p>
                          <p className={`text-[10px] ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>Gateways</p>
                        </div>
                        <div>
                          <p className={`text-lg font-bold ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{edgeCount}</p>
                          <p className={`text-[10px] ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>Edges</p>
                        </div>
                      </div>

                      {usedBy.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {usedBy.map(p => (
                            <span key={p.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] ${isDark ? 'bg-brand-500/10 text-brand-400 border border-brand-500/20' : 'bg-brand-50 text-brand-600'}`}>
                              <Package className="w-3 h-3" /> {p.name}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className={`flex items-center justify-between text-[10px] ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                        <span>{formatDate(t.created_at)}</span>
                        <span>{usedBy.length} product{usedBy.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className={`flex border-t ${isDark ? 'border-zinc-800' : 'border-zinc-100'}`}>
                      <button
                        onClick={() => navigate(`/workflows/${t.id}`)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${isDark ? 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50'}`}
                        data-testid={`edit-template-${t.id}`}
                      >
                        <PencilSimple className="w-3.5 h-3.5" /> Edit
                      </button>
                      <div className={`w-px ${isDark ? 'bg-zinc-800' : 'bg-zinc-100'}`} />
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-red-500 hover:bg-red-500/10 transition-colors"
                        data-testid={`delete-template-${t.id}`}
                      >
                        <Trash className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Assign Dialog */}
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white border-zinc-200'} max-w-md`}>
            <DialogHeader>
              <DialogTitle className="font-heading">Assign Workflow to Product</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                Link a workflow template to a product. QR codes generated for this product will follow the workflow.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Product *</label>
                <Select value={assignProductId} onValueChange={setAssignProductId}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="assign-product-select">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.batch_number})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Workflow *</label>
                <Select value={assignTemplateId} onValueChange={setAssignTemplateId}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="assign-template-select">
                    <SelectValue placeholder="Select workflow" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} (v{t.version || 1})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignOpen(false)} className={isDark ? 'border-zinc-700 text-zinc-300' : ''}>Cancel</Button>
              <Button onClick={handleAssign} disabled={assigning || !assignProductId || !assignTemplateId} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="confirm-assign-btn">
                {assigning ? <CircleNotch className="w-4 h-4 animate-spin" /> : 'Assign'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
